// Dev-only error aggregator. Captures console.error/warn and fetch 4xx/5xx.
// No-op in production builds.

export type DevLogEntry = {
  id: string;
  type: 'console.error' | 'console.warn' | 'network';
  message: string;
  detail?: string;
  url?: string;
  status?: number;
  count: number;
  firstAt: number;
  lastAt: number;
};

type Listener = (entries: DevLogEntry[]) => void;

const entries = new Map<string, DevLogEntry>();
const listeners = new Set<Listener>();
let installed = false;

const notify = () => {
  const arr = Array.from(entries.values()).sort((a, b) => b.lastAt - a.lastAt);
  listeners.forEach((l) => l(arr));
};

const upsert = (key: string, partial: Omit<DevLogEntry, 'id' | 'count' | 'firstAt' | 'lastAt'>) => {
  const now = Date.now();
  const existing = entries.get(key);
  if (existing) {
    existing.count += 1;
    existing.lastAt = now;
  } else {
    entries.set(key, { id: key, count: 1, firstAt: now, lastAt: now, ...partial });
  }
  notify();
};

export const subscribeDevLogs = (listener: Listener) => {
  listeners.add(listener);
  listener(Array.from(entries.values()).sort((a, b) => b.lastAt - a.lastAt));
  return () => {
    listeners.delete(listener);
  };
};

export const clearDevLogs = () => {
  entries.clear();
  notify();
};

export const installDevErrorLogger = () => {
  if (installed || typeof window === 'undefined') return;
  if (!import.meta.env.DEV) return;
  installed = true;

  const origError = console.error.bind(console);
  const origWarn = console.warn.bind(console);

  console.error = (...args: unknown[]) => {
    const message = args.map(stringify).join(' ');
    upsert(`err:${message.slice(0, 200)}`, { type: 'console.error', message });
    origError(...args);
  };

  console.warn = (...args: unknown[]) => {
    const message = args.map(stringify).join(' ');
    upsert(`warn:${message.slice(0, 200)}`, { type: 'console.warn', message });
    origWarn(...args);
  };

  const origFetch = window.fetch.bind(window);
  window.fetch = async (input: RequestInfo | URL, init?: RequestInit) => {
    const url = typeof input === 'string' ? input : input instanceof URL ? input.toString() : input.url;
    try {
      const res = await origFetch(input as RequestInfo, init);
      if (!res.ok) {
        upsert(`net:${res.status}:${stripQuery(url)}`, {
          type: 'network',
          message: `${res.status} ${res.statusText} — ${stripQuery(url)}`,
          url,
          status: res.status,
          detail: url,
        });
      }
      return res;
    } catch (err) {
      const message = err instanceof Error ? err.message : String(err);
      upsert(`net:fail:${stripQuery(url)}`, {
        type: 'network',
        message: `Network failure — ${message}`,
        url,
        detail: url,
      });
      throw err;
    }
  };

  window.addEventListener('error', (e) => {
    upsert(`win:err:${e.message}`, { type: 'console.error', message: e.message, detail: e.filename });
  });

  window.addEventListener('unhandledrejection', (e) => {
    const message = e.reason instanceof Error ? e.reason.message : String(e.reason);
    upsert(`win:rej:${message}`, { type: 'console.error', message: `Unhandled rejection: ${message}` });
  });
};

const stringify = (v: unknown): string => {
  if (v instanceof Error) return v.message;
  if (typeof v === 'string') return v;
  try {
    return JSON.stringify(v);
  } catch {
    return String(v);
  }
};

const stripQuery = (url: string) => url.split('?')[0];
